const DEBUG = process.env.NODE_ENV !== 'production';

function debugLog(message, details = null) {
  if (DEBUG) {
    const timestamp = new Date().toISOString();
    if (details) {
      console.debug(`[API ${timestamp}] ${message}`, details);
    } else {
      console.debug(`[API ${timestamp}] ${message}`);
    }
  }
}

const ERROR_CODES = {
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SESSION_INVALID: 'SESSION_INVALID',
  NETWORK_ERROR: 'NETWORK_ERROR',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  UNAUTHORIZED: 'UNAUTHORIZED',
  ACCESS_DENIED: 'ACCESS_DENIED',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',
};

function getSessionFromStorage() {
  const stored = sessionStorage.getItem('auth_session');
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to parse session:', error);
    return null;
  }
}

function isSessionValid(sessionObj) {
  if (!sessionObj) {
    return false;
  }

  const now = Date.now();

  if (sessionObj.expiresAt && now >= sessionObj.expiresAt) {
    debugLog('Session invalid: expiry reached');
    return false;
  }

  if (!sessionObj.token || !sessionObj.user) {
    debugLog('Session invalid: missing required fields');
    return false;
  }

  return true;
}

let authContextGetter = null;

export function setAuthContextGetter(getter) {
  authContextGetter = getter;
}

function getAuthContext() {
  if (!authContextGetter) {
    throw new Error(
      'authContextGetter not initialized. Call setAuthContextGetter() in your app initialization.'
    );
  }
  return authContextGetter();
}

async function prepareRequest() {
  const session = getSessionFromStorage();

  if (!isSessionValid(session)) {
    debugLog('Pre-request validation failed: session invalid');

    // Call requestReauth from AuthContext
    try {
      const authContext = getAuthContext();
      authContext.requestReauth();
    } catch (error) {
      console.warn('Failed to call requestReauth:', error);
    }

    return {
      error: {
        code: ERROR_CODES.SESSION_INVALID,
        message: 'Session is invalid or expired',
        status: 401,
      },
      token: null,
    };
  }

  return {
    error: null,
    token: session.token,
  };
}

function handle401(errorBody) {
  debugLog('Handling 401 Unauthorized');

  try {
    const authContext = getAuthContext();
    authContext.requestReauth();
  } catch (error) {
    console.warn('Failed to call requestReauth on 401:', error);
  }

  return {
    success: false,
    status: 401,
    code: ERROR_CODES.UNAUTHORIZED,
    message: 'Unauthenticated or session expired',
    details: errorBody,
  };
}

function handle403(errorBody) {
  debugLog('Handling 403 Forbidden');

  // NOTE: Do NOT logout or clear session on 403
  // User is authenticated but lacks permission

  return {
    success: false,
    status: 403,
    code: ERROR_CODES.ACCESS_DENIED,
    message: 'Insufficient permissions or access denied',
    details: errorBody,
  };
}

export async function apiRequest(path, options = {}) {
  // Pre-request validation
  const prepResult = await prepareRequest();
  if (prepResult.error) {
    debugLog(`Request blocked: ${prepResult.error.code}`, { path });
    return {
      data: null,
      error: prepResult.error,
      status: 401,
    };
  }

  const token = prepResult.token;

  // Prepare headers with automatic token injection
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    debugLog('Authorization header injected');
  }

  debugLog(`Starting ${options.method || 'GET'} request`, { path });

  // Execute request
  let response;
  try {
    response = await fetch(path, { ...options, headers });
  } catch (err) {
    debugLog(`Network error: ${err.message}`, { path });
    return {
      data: null,
      error: {
        code: ERROR_CODES.NETWORK_ERROR,
        message: 'Network request failed',
        details: err.message,
      },
      status: 0,
    };
  }

  // Handle error responses
  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { error: ERROR_CODES.INVALID_RESPONSE };
    }

    debugLog(`HTTP ${response.status} error`, { path, errorBody });

    // Route 401 to centralized handler
    if (response.status === 401) {
      return {
        data: null,
        error: handle401(errorBody),
        status: 401,
      };
    }

    // Route 403 to centralized handler (without logout)
    if (response.status === 403) {
      return {
        data: null,
        error: handle403(errorBody),
        status: 403,
      };
    }

    // Handle other errors
    const errorCode =
      response.status === 404
        ? ERROR_CODES.NOT_FOUND
        : response.status >= 500
          ? ERROR_CODES.SERVER_ERROR
          : ERROR_CODES.INVALID_RESPONSE;

    return {
      data: null,
      error: {
        code: errorCode,
        message: `HTTP ${response.status} error`,
        status: response.status,
        details: errorBody,
      },
      status: response.status,
    };
  }

  // Handle success response
  try {
    const data = await response.json();
    debugLog(`Request successful`, { path, status: response.status });
    return {
      data,
      error: null,
      status: response.status,
    };
  } catch (err) {
    debugLog(`Failed to parse response: ${err.message}`, { path });
    return {
      data: null,
      error: {
        code: ERROR_CODES.INVALID_RESPONSE,
        message: 'Failed to parse response JSON',
        details: err.message,
      },
      status: response.status,
    };
  }
}


export const apiGet = (path, options = {}) =>
  apiRequest(path, { ...options, method: 'GET' });

export const apiPost = (path, body, options = {}) =>
  apiRequest(path, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });

export const apiPut = (path, body, options = {}) =>
  apiRequest(path, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const apiDelete = (path, options = {}) =>
  apiRequest(path, { ...options, method: 'DELETE' });

export const apiPatch = (path, body, options = {}) =>
  apiRequest(path, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(body),
  });


export { ERROR_CODES };
